export type MediaFile = {
  id: string;
  code: string;
  title: string;
  description: string | null;
  originalName: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  fileType: string;
  extension: string;
  tags: string[];
  visibility: string;
  version: number;
  downloadCount: number;
  isExternal: boolean;
  externalUrl: string | null;

  entityType: string | null;
  entityId: string | null;
  entityLabel: string | null;
  phaseId: string | null;
  phaseName: string | null;
  stageId: string | null;
  stageName: string | null;

  companyId: string | null;
  companyName: string | null;
  productId: string | null;
  productName: string | null;
  projectId: string | null;
  projectName: string | null;
  /** Combined links from the multi-select Linked To picker. Includes the
   *  primary FK (e.g. companyId) as the first element when present, plus
   *  any supplemental entries from linkedXxxIds. */
  linkedCompanies: Array<{ id: string; name: string }>;
  linkedProducts: Array<{ id: string; name: string }>;
  linkedProjects: Array<{ id: string; name: string }>;
  meetingId: string | null;
  meetingTitle: string | null;
  taskId: string | null;
  taskTitle: string | null;
  actionId: string | null;
  actionTitle: string | null;
  noteId: string | null;
  noteTitle: string | null;
  milestoneId: string | null;
  milestoneTitle: string | null;
  budgetId: string | null;
  budgetName: string | null;
  expenseId: string | null;
  expenseTitle: string | null;

  collectionId: string | null;
  collectionName: string | null;

  uploadedById: string | null;
  uploadedByName: string | null;

  createdAt: string;
  updatedAt: string;
};

/**
 * A file "needs attention" when it has no description, no tags, and no
 * Linked To entity (company / product / project). Just having a title
 * (which defaults to the filename on upload) does not count as enough
 * metadata. Returns true → render the small indicator and tooltip.
 */
export function mediaNeedsAttention(file: MediaFile): boolean {
  const hasDescription = !!(file.description && file.description.trim());
  const hasTags = Array.isArray(file.tags) && file.tags.length > 0;
  const hasLinks =
    file.linkedCompanies.length > 0 ||
    file.linkedProducts.length > 0 ||
    file.linkedProjects.length > 0;
  return !hasDescription && !hasTags && !hasLinks;
}

export type MediaFileVersion = {
  id: string;
  version: number;
  fileName: string;
  filePath: string;
  fileSize: number;
  uploadedById: string | null;
  uploadedByName: string | null;
  createdAt: string;
};

export type MediaFileDetail = MediaFile & {
  versions: MediaFileVersion[];
};

export type MediaCollection = {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  isSystem: boolean;
  sortOrder: number;
  fileCount: number;
  createdById: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
};

export type MediaStats = {
  totalFiles: number;
  totalSize: number;
  byType: Record<string, number>;
  recentUploads: number;
  storageByEntity: Array<{
    entityType: string;
    entityId: string;
    entityLabel: string | null;
    count: number;
  }>;
};

export type MediaListResponse = {
  data: MediaFile[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  meta: {
    totalFiles: number;
    totalSize: number;
    byType: Record<string, number>;
  };
};

export type MediaListParams = {
  page?: number;
  limit?: number;
  search?: string;
  fileType?: string;
  tags?: string[];
  companyId?: string;
  productId?: string;
  projectId?: string;
  meetingId?: string;
  taskId?: string;
  actionId?: string;
  noteId?: string;
  milestoneId?: string;
  budgetId?: string;
  expenseId?: string;
  entityType?: string;
  entityId?: string;
  includeChildren?: boolean;
  phaseId?: string;
  stageId?: string;
  collectionId?: string;
  uploadedById?: string;
  myFiles?: boolean;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
};

export type UploadFileInput = {
  file: File;
  title?: string;
  description?: string;
  tags?: string[];
  visibility?: string;
  collectionId?: string;
  entityType?: string;
  entityId?: string;
  entityLabel?: string;
  phaseId?: string;
  stageId?: string;
  companyId?: string;
  productId?: string;
  projectId?: string;
  companyIds?: string[];
  productIds?: string[];
  projectIds?: string[];
  meetingId?: string;
  taskId?: string;
  actionId?: string;
  noteId?: string;
  milestoneId?: string;
  budgetId?: string;
  expenseId?: string;
};

export type CreateExternalLinkPayload = {
  title: string;
  externalUrl: string;
  description?: string;
  tags?: string[];
  visibility?: string;
  collectionId?: string;
  entityType?: string;
  entityId?: string;
  entityLabel?: string;
  companyId?: string;
  productId?: string;
  projectId?: string;
  companyIds?: string[];
  productIds?: string[];
  projectIds?: string[];
  meetingId?: string;
  taskId?: string;
  actionId?: string;
  noteId?: string;
  milestoneId?: string;
  budgetId?: string;
  expenseId?: string;
};

export type UpdateMediaPayload = Partial<{
  title: string;
  description: string | null;
  tags: string[];
  visibility: string;
  collectionId: string | null;
  entityType: string | null;
  entityId: string | null;
  entityLabel: string | null;
  companyId: string | null;
  productId: string | null;
  projectId: string | null;
  companyIds: string[];
  productIds: string[];
  projectIds: string[];
  createdAt: string;
  meetingId: string | null;
  taskId: string | null;
  actionId: string | null;
  noteId: string | null;
  milestoneId: string | null;
  budgetId: string | null;
  expenseId: string | null;
}>;

export type CreateCollectionPayload = {
  name: string;
  description?: string;
  color?: string;
  icon?: string;
};

export type UpdateCollectionPayload = Partial<{
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
}>;

export type MediaComment = {
  id: string;
  content: string;
  authorId: string | null;
  authorName: string | null;
  createdAt: string;
  updatedAt: string;
};

// ── Tree view ───────────────────────────────────────────────

export type MediaTreeEntityRef = {
  type: "COMPANY" | "PRODUCT" | "PROJECT";
  id: string;
  name: string;
  referenceId: string | null;
};

export type MediaTreePhaseGroup = {
  phase: { id: string; name: string };
  documents: MediaFile[];
  stages: {
    stage: { id: string; name: string };
    documents: MediaFile[];
  }[];
};

export type MediaTreeNode = {
  entity: MediaTreeEntityRef;
  documents: MediaFile[];
  documentsTruncated: boolean;
  children: MediaTreeNode[];
  phaseGroups?: MediaTreePhaseGroup[];
};

export type MediaTreeParams = {
  rootEntityType?: "COMPANY" | "PRODUCT" | "PROJECT";
  rootEntityId?: string;
  search?: string;
  fileType?: string;
  tags?: string[];
  uploadedById?: string;
  dateFrom?: string;
  dateTo?: string;
  perNodeLimit?: number;
};

export type MediaTreeResponse = {
  nodes: MediaTreeNode[];
};
